<template>
  <div class="online-equipment-tab">
    <!-- Statistic Cards -->
    <el-row :gutter="16" class="oec-metric-row">
      <el-col :xl="6" :lg="12" :md="12">
        <div class="oec-metric-card oec-metric-total">
          <span class="oec-metric-label">Device Quantity</span>
          <span class="oec-metric-value oec-value-blue">{{ metrics.deviceQty }}</span>
          <span class="oec-metric-unit">Devices</span>
        </div>
      </el-col>
      <el-col :xl="6" :lg="12" :md="12">
        <div class="oec-metric-card oec-metric-warranty">
          <span class="oec-metric-label">Devices Under Warranty</span>
          <span class="oec-metric-value oec-value-green">{{ metrics.warrantyQty }}</span>
          <span class="oec-metric-unit">Devices</span>
        </div>
      </el-col>
      <el-col :xl="6" :lg="12" :md="12">
        <div class="oec-metric-card oec-metric-expired">
          <span class="oec-metric-label">Devices Expired Warranty</span>
          <span class="oec-metric-value oec-value-red">{{ metrics.expiredQty }}</span>
          <span class="oec-metric-unit">Devices</span>
        </div>
      </el-col>
      <el-col :xl="6" :lg="12" :md="12">
        <div class="oec-metric-card oec-metric-expiring">
          <span class="oec-metric-label">Devices Expiring in Next 6 Months</span>
          <span class="oec-metric-value oec-value-orange">{{ metrics.expiringQty }}</span>
          <span class="oec-metric-unit">Devices</span>
        </div>
      </el-col>
    </el-row>

    <!-- Query Area -->
    <el-form :model="query" ref="queryForm" label-position="left" label-width="160px" class="oec-query" @keyup.enter.native="handleSearch">
      <el-row>
        <el-col :xl="8" :lg="8" :md="12">
          <el-form-item label="Project Name">
            <el-input clearable size="small" v-model="query.projectName" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :xl="8" :lg="8" :md="12">
          <el-form-item label="Product Name">
            <el-input clearable size="small" v-model="query.productName" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :xl="8" :lg="8" :md="12">
          <el-form-item label="High/Low End Classification" class="oec-long-label">
            <el-select clearable size="small" v-model="query.classification" placeholder="Please select" style="width:100%">
              <el-option v-for="c in classificationOptions" :key="c.value" :label="c.label" :value="c.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xl="8" :lg="8" :md="12">
          <el-form-item label="Warranty & Maintenance Expiry Date" class="oec-long-label">
            <el-date-picker
              clearable
              size="small"
              v-model="query.expiryDateRange"
              type="daterange"
              range-separator="—"
              start-placeholder="Start Date"
              end-placeholder="End Date"
              style="width:100%"
            ></el-date-picker>
          </el-form-item>
        </el-col>
        <!-- Actions -->
        <el-col :xl="16" :lg="16" :md="24" class="oec-btn-col">
          <el-button type="primary" size="small" @click="handleSearch">Search</el-button>
          <el-button size="small" @click="handleReset">Reset</el-button>
          <el-button size="small" :loading="exportLoading" @click="handleExport">Export</el-button>
        </el-col>
      </el-row>
    </el-form>

    <!-- Table -->
    <el-table
      :data="tableData"
      v-loading="tableLoading"
      size="small"
      border
      style="width:100%"
      :header-cell-style="{background:'#F5F5F9'}"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="projectName" label="Project Name" min-width="200" sortable="custom" align="left" fixed="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="productName" label="Product Name" min-width="180" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="classification" label="High/Low End Classification" width="230" sortable="custom" align="center" :show-overflow-tooltip="true">
        <template slot-scope="scope">
          <span v-if="scope.row.classification" :class="['oec-tag', 'oec-tag-' + classTagClass(scope.row.classification)]">{{ scope.row.classification }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="warrantyExpiryDate" label="Warranty & Maintenance Expiry Date" width="300" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="remark" label="Remark(Base)" min-width="180" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column label="Modify" width="110" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="primary" size="mini" @click="handleModify(scope.row)">Modify</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <div class="oec-pagination">
      <span class="oec-pagination-total">Total {{ total }}</span>
      <el-pagination
        small
        layout="prev, pager, next, sizes"
        :total="total"
        :current-page.sync="currentPage"
        :page-sizes="[10, 20, 50]"
        :page-size.sync="pageSize"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      ></el-pagination>
    </div>

    <!-- Modify Remark Modal -->
    <el-dialog title="Modify Remark" :visible.sync="modifyVisible" width="480px">
      <el-form label-position="left" label-width="70px">
        <el-form-item label="Remark:">
          <el-input type="textarea" :rows="4" v-model="modifyRemark" placeholder="Please input remark" maxlength="500" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="modifyVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" :loading="modifySaving" @click="saveModify" style="margin-left: 6px">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'OnlineEquipmentTab',
  props: {
    accountInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Metrics
      metrics: {
        deviceQty: 0,
        warrantyQty: 0,
        expiredQty: 0,
        expiringQty: 0,
      },
      // Query
      query: {
        projectName: '',
        productName: '',
        classification: '',
        expiryDateRange: null,
      },
      classificationOptions: [],
      // Table
      tableData: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      sortField: '',
      sortOrder: '',
      tableLoading: false,
      // Export
      exportLoading: false,
      // Modify modal
      modifyVisible: false,
      modifyRow: null,
      modifyRemark: '',
      modifySaving: false,
    }
  },
  watch: {
    'accountInfo.id'(val) {
      if (val) {
        this.loadStatistics()
        this.loadData()
      }
    }
  },
  created() {
    this.initDropdownData()
    if (this.accountInfo.id) {
      this.loadStatistics()
      this.loadData()
    }
  },
  methods: {
    initDropdownData() {
      const kvUrl = `${this.$store.state.islhurl}keyValue/queryLabelList?keyList=Classification`
      this._get(kvUrl).then(res => {
        if (res.code === 200 && res.data) {
          this.classificationOptions = res.data.Classification || []
        }
      }).catch(() => {})
    },
    loadStatistics() {
      const id = this.accountInfo.id
      if (!id) return
      const params = this.buildQueryParams()
      let url = `${this.$store.state.islhurl}valued-account/business/${id}/equipment/statistics`
      const qs = this.buildQueryString(params)
      if (qs) url += '?' + qs
      this._get(url).then(res => {
        if (res.code === 200 && res.data) {
          this.metrics = {
            deviceQty: res.data.deviceQuantity || 0,
            warrantyQty: res.data.devicesUnderWarranty || 0,
            expiredQty: res.data.devicesExpiredWarranty || 0,
            expiringQty: res.data.devicesExpiringIn6Months || 0,
          }
        }
      }).catch(() => {})
    },
    loadData() {
      const id = this.accountInfo.id
      if (!id) return
      this.tableLoading = true
      const params = this.buildQueryParams()
      params.pageNum = this.currentPage
      params.pageSize = this.pageSize
      let url = `${this.$store.state.islhurl}valued-account/business/${id}/equipment`
      const qs = this.buildQueryString(params)
      if (qs) url += '?' + qs
      this._get(url).then(res => {
        this.tableLoading = false
        if (res.code === 200) {
          this.tableData = res.data || []
          this.total = res.totalSize || 0
        }
      }).catch(() => {
        this.tableLoading = false
      })
    },
    buildQueryParams() {
      const params = {}
      if (this.query.projectName) params.projectName = this.query.projectName
      if (this.query.productName) params.productName = this.query.productName
      if (this.query.classification) params.classification = this.query.classification
      if (this.query.expiryDateRange && this.query.expiryDateRange.length === 2) {
        const [start, end] = this.query.expiryDateRange
        params.warrantyExpiryStart = this.formatDate(start)
        params.warrantyExpiryEnd = this.formatDate(end)
      }
      if (this.sortField) {
        const fieldMap = {
          projectName: 'projectName',
          productName: 'productName',
          classification: 'classification',
          warrantyExpiryDate: 'warrantyExpiryDate',
          remark: 'remark',
        }
        params.sortField = fieldMap[this.sortField] || this.sortField
        params.sortOrder = this.sortOrder === 'ascending' ? 'asc' : 'desc'
      }
      return params
    },
    buildQueryString(params) {
      return Object.keys(params)
        .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&')
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    handleSearch() {
      this.currentPage = 1
      this.loadStatistics()
      this.loadData()
    },
    handleReset() {
      this.query = {
        projectName: '',
        productName: '',
        classification: '',
        expiryDateRange: null,
      }
      this.sortField = ''
      this.sortOrder = ''
      this.currentPage = 1
      this.loadStatistics()
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.sortField = order ? (prop || '') : ''
      this.sortOrder = order || ''
      this.currentPage = 1
      this.loadData()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadData()
    },
    classTagClass(classification) {
      return { 'High-end': 'green', 'Mid/High-end': 'orange', 'High-end Product': 'green', 'Mid/High-end Product': 'orange', 'Mid-end': 'blue', 'Mid-end Product': 'blue', 'Low-end': 'gray', 'Low-end Product': 'gray' }[classification] || 'gray'
    },
    handleModify(row) {
      this.modifyRow = row
      this.modifyRemark = row.remark || ''
      this.modifyVisible = true
    },
    saveModify() {
      if (!this.modifyRow || !this.modifyRow.equipmentId) return
      const id = this.accountInfo.id
      if (!id) return
      this.modifySaving = true
      const url = `${this.$store.state.islhurl}valued-account/business/${id}/equipment/${this.modifyRow.equipmentId}`
      this._put(url, { remark: this.modifyRemark }).then(res => {
        this.modifySaving = false
        if (res.code === 200) {
          this.$message.success('Remark updated successfully!')
          this.modifyVisible = false
          this.loadData()
        } else {
          this.$message.error(res.msg || 'Update failed!')
        }
      }).catch(() => {
        this.modifySaving = false
        this.$message.error('Update failed!')
      })
    },
    handleExport() {
      const id = this.accountInfo.id
      if (!id) return
      this.exportLoading = true
      const params = this.buildQueryParams()
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') delete params[key]
      })
      this.$axios({
        url: `${this.$store.state.islhurl}valued-account/business/${id}/equipment/export`,
        method: 'get',
        params,
        responseType: 'blob'
      }).then(response => {
        this.exportLoading = false
        const blob = new Blob([response.data])
        const aEle = document.createElement('a')
        const href = window.URL.createObjectURL(blob)
        aEle.href = href
        const d = new Date()
        const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
        aEle.download = 'OnlineEquipment_' + dateStr + '.xlsx'
        document.body.appendChild(aEle)
        aEle.click()
        document.body.removeChild(aEle)
        window.URL.revokeObjectURL(href)
      }).catch(() => {
        this.exportLoading = false
        this.$message.error('Export failed!')
      })
    },
  },
}
</script>

<style lang="less" scoped>
.online-equipment-tab {
  // Metric Cards
  .oec-metric-row {
    margin-bottom: 16px;
    display: flex;
    flex-wrap: wrap;
    &::after {
      display: none;
    }
    /deep/ .el-col {
      display: flex;
      float: none;
    }
  }
  .oec-metric-card {
    flex: 1;
    background: #fff;
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    padding: 12px 16px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    display: flex;
    align-items: center;
    gap: 8px;
    transition: box-shadow 0.15s;
    margin-bottom: 10px;
    &:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }
  }
  .oec-metric-total {
    border-left: 3px solid #007AFF;
    background: #E8F4FF;
  }
  .oec-metric-warranty {
    border-left: 3px solid #52C41A;
    background: #F0FFF0;
  }
  .oec-metric-expired {
    border-left: 3px solid #FF4D4F;
    background: #FFF1F0;
  }
  .oec-metric-expiring {
    border-left: 3px solid #FAAD14;
    background: #FFFBE6;
  }
  .oec-metric-label {
    font-size: 13px;
    color: #6B7280;
    font-weight: 600;
    margin-right: 8px;
    white-space: nowrap;
  }
  .oec-metric-value {
    font-size: 28px;
    font-weight: 600;
    line-height: 1;
    margin-right: 4px;
  }
  .oec-value-blue { color: #007AFF; }
  .oec-value-green { color: #52C41A; }
  .oec-value-red { color: #FF4D4F; }
  .oec-value-orange { color: #FAAD14; }
  .oec-metric-unit {
    font-size: 12px;
    color: #6B7280;
  }

  // Query
  .oec-query {
    padding: 8px 0 5px 0;
    /deep/ .el-col {
      margin-bottom: 7px;
    }
    /deep/ .el-form-item {
      margin-bottom: 0;
      // padding: 0 15px 0 0;
    }
    /deep/ .el-form-item__label {
      font-size: 13px;
      color: #1F2937;
      text-align: right;
    }
    /deep/ .oec-long-label .el-form-item__label {
      line-height: 16px;
      padding-top: 4px;
    }
    /deep/ .el-select {
      width: 100%;
    }
    /deep/ .el-form-item__content .el-input__suffix {
      left: auto;
      right: 10px;
    }
    /deep/ .el-range-editor.el-input__inner {
      width: 100%;
    }
  }
  .oec-btn-col {
    padding-top: 0;
    text-align: right;
    /deep/ .el-button + .el-button {
      margin-left: 10px;
    }
  }

  // Table Tags
  .oec-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 12px;
    line-height: 20px;
    &.oec-tag-green { background: #f6ffed; color: #52C41A; border: 1px solid #b7eb8f; }
    &.oec-tag-blue { background: #e6f7ff; color: #1890FF; border: 1px solid #91d5ff; }
    &.oec-tag-orange { background: #fff7e6; color: #FA8C16; border: 1px solid #ffd591; }
    &.oec-tag-red { background: #fff1f0; color: #F5222D; border: 1px solid #ffa39e; }
    &.oec-tag-gray { background: #fafafa; color: #8C8C8C; border: 1px solid #d9d9d9; }
  }

  // Pagination
  .oec-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 0 0;
    gap: 12px;
    .oec-pagination-total {
      font-size: 13px;
      color: #6B7280;
    }
  }
}
.el-button+.el-button, .el-checkbox.is-bordered+.el-checkbox.is-bordered {
  margin-left: 0;
}
</style>



// WEBPACK FOOTER //
// src/view/valuedAccountManagement/accountDetailTabs/OnlineEquipment.vue